import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkles, Trophy, ChevronLeft, ChevronRight, PartyPopper, MessageCircle, Send, User } from "lucide-react";
import { trophiesApi, socialApi, errMsg } from "@/lib/api";
import { UserAvatar } from "@/components/shared";
import { Skeleton } from "@/components/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const monthLabel = (m) => { if (!m) return ""; const [y, mm] = m.split("-"); return `${MONTHS[parseInt(mm, 10) - 1]} ${y}`; };
const TITLE = { athlete: "Athlète du mois", coach: "Coach du mois" };

function VedetteCard({ v, canInteract }) {
  const [liked, setLiked] = useState(v.liked);
  const [likes, setLikes] = useState(v.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(v.comments_count);
  const [text, setText] = useState("");

  const felicit = async () => {
    if (!canInteract) { toast.error("Réservé aux membres actifs"); return; }
    const next = !liked;
    setLiked(next); setLikes((n) => Math.max(0, n + (next ? 1 : -1))); // optimistic
    try { const r = await socialApi.like(v.post_id); setLiked(r.data.liked); setLikes(r.data.likes_count); if (r.data.liked) toast.success("Félicitations envoyées \uD83D\uDC4F"); }
    catch (e) { setLiked(!next); setLikes((n) => Math.max(0, n + (next ? -1 : 1))); toast.error(errMsg(e)); }
  };
  const loadComments = async () => { const r = await socialApi.comments(v.post_id); setComments(r.data); setCount(r.data.length); };
  const toggle = async () => { const n = !showComments; setShowComments(n); if (n) loadComments(); };
  const add = async () => { if (!text.trim()) return; try { await socialApi.addComment(v.post_id, text); setText(""); loadComments(); } catch (e) { toast.error(errMsg(e)); } };

  const name = v.recipient ? `${v.recipient.first_name} ${v.recipient.last_name}` : "—";
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="w-[300px] shrink-0 snap-center sm:w-[340px]" role="option" aria-label={`${TITLE[v.type]} ${name}`}>
      <Card className="flex h-full flex-col overflow-hidden" data-testid="vedette-carte">
        <div className="relative asl-hero-gradient asl-noise p-4 text-white">
          <div className="absolute right-3 top-3"><Trophy className="h-5 w-5 text-trophy-gold" /></div>
          <div className="flex items-center gap-3">
            <UserAvatar user={v.recipient} size="h-16 w-16" className="ring-2 ring-white/25" />
            <div className="min-w-0">
              <Badge className="mb-1 border-0 bg-trophy-gold/20 text-trophy-gold">{TITLE[v.type]}</Badge>
              <div className="truncate font-heading text-lg font-bold leading-tight">{name}</div>
              <div className="text-xs text-white/70">{v.sport_name} · {monthLabel(v.month)}</div>
            </div>
          </div>
        </div>
        <CardContent className="flex flex-1 flex-col gap-3 p-4">
          {v.reason && <p className="text-sm text-muted-foreground">« {v.reason} »</p>}
          <div className="flex items-center gap-1.5 text-sm"><Trophy className="h-4 w-4 text-trophy-gold" /><span className="font-medium">{v.recipient?.total_trophies || 0}</span><span className="text-muted-foreground">trophée(s)</span></div>
          <div className="mt-auto flex items-center gap-2">
            <Button size="sm" variant={liked ? "default" : "outline"} onClick={felicit} className={liked ? "bg-secondary text-white hover:bg-emerald-600" : ""} data-testid="vedette-feliciter"><PartyPopper className="mr-1.5 h-4 w-4" />{likes}</Button>
            <Button size="sm" variant="outline" onClick={toggle} data-testid="vedette-commentaires"><MessageCircle className="mr-1.5 h-4 w-4" />{count}</Button>
            <Link to={`/profil/${v.recipient?.id}`} className="ml-auto"><Button size="sm" variant="ghost" data-testid="vedette-profil"><User className="mr-1.5 h-4 w-4" />Profil</Button></Link>
          </div>
          {showComments && (
            <div className="space-y-2 border-t pt-3">
              {comments.length === 0 && <div className="text-xs text-muted-foreground">Aucun commentaire. Soyez le premier à féliciter !</div>}
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2"><UserAvatar user={c.author} size="h-6 w-6" /><div className="rounded-lg bg-muted px-2.5 py-1"><div className="text-[11px] font-semibold">{c.author?.first_name} {c.author?.last_name}</div><div className="text-xs">{c.content}</div></div></div>
              ))}
              {canInteract && (
                <div className="flex items-center gap-2"><Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Féliciter en commentaire…" className="min-h-0 py-1.5 text-sm" data-testid="vedette-commentaire-input" /><Button size="icon" onClick={add} className="h-9 w-9 bg-secondary text-white hover:bg-emerald-600" data-testid="vedette-commentaire-envoyer"><Send className="h-4 w-4" /></Button></div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function VedettesDuMois({ canInteract }) {
  const scroller = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => { (async () => { setLoading(true); try { const r = await trophiesApi.monthlyCurrent(); setData(r.data); } catch { setData({ items: [] }); } finally { setLoading(false); } })(); }, []);

  const items = data?.items || [];
  const scrollByDir = (dir) => { const el = scroller.current; if (!el) return; el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" }); };
  const onScroll = () => { const el = scroller.current; if (!el || !items.length) return; const i = Math.round(el.scrollLeft / (el.scrollWidth / items.length)); setActive(Math.min(items.length - 1, Math.max(0, i))); };
  const onKey = (e) => { if (e.key === "ArrowRight") scrollByDir(1); else if (e.key === "ArrowLeft") scrollByDir(-1); };

  if (loading) return <div className="flex gap-3 overflow-hidden">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-64 w-[300px] shrink-0 rounded-2xl sm:w-[340px]" />)}</div>;
  if (!items.length) return null;

  return (
    <section className="space-y-3" aria-label="Vedettes du mois">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Sparkles className="h-5 w-5 text-trophy-gold" />Vedettes du mois</h2>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => scrollByDir(-1)} aria-label="Précédent" data-testid="vedettes-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => scrollByDir(1)} aria-label="Suivant" data-testid="vedettes-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div ref={scroller} onScroll={onScroll} tabIndex={0} onKeyDown={onKey} role="listbox" aria-label="Carrousel des vedettes du mois"
           className="flex snap-x snap-mandatory gap-3 overflow-x-auto no-scrollbar pb-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl">
        {items.map((v) => <VedetteCard key={v.id} v={v} canInteract={canInteract} />)}
      </div>
      {items.length > 1 && (
        <div className="flex justify-center gap-1.5" aria-hidden="true">
          {items.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === active ? "w-5 bg-secondary" : "w-1.5 bg-muted-foreground/30"}`} />)}
        </div>
      )}
    </section>
  );
}
